import {AxiosRequestConfig} from 'axios';

import {Address, Dish, MoneycardOtlRuleManagement, OrderData, OrderScooberJob, RestaurantFromGet, User} from '~/shared/store/models';
import {ActiveOrder} from '~/shared/types/ActiveOrderBannerTypes';
import {DeliveryMethods} from '~/shared/consts/restaurantConsts';
import {ISystemAnnouncement} from '~/shared/store/models/SystemAnnouncement';

import {get, patch, post, apiDelete, put} from '../http';
import {validateAxiosAndReturnResponse} from '../util';

import {
  IRestaurantGroupsParams,
  IGetRestaurantMenuParams,
  IGetRestaurantParams,
  IOrderParams,
  IMarketingBannerParams,
  ILoadTenbisCredit,
  IUpdateUserParams,
  IUserConfig,
  IFetchBrandingPageDataParams,
  IAutoCreditSubscription,
  UpdateUserNotificationsPreferencesParams,
  UpsertOverTheLimitRuleParams,
} from './interfaces/payloads';
import {
  IRestaurantGroupsRes,
  IRestaurantMenuRes,
  IMarketingBannerRes,
  IOrderRes,
  IRefreshTokenRes,
  ILoadTenbisCreditResponse,
  IUpdateUserRes,
  IFetchBrandingPageDataResponse,
  IDiscountResponse,
  ApplePayMerchantSessionResponse,
  TrackerContextResponse,
  TrackerRouteResponse,
  IAutoCreditSubscriptionResponse,
  IUnusedVouchersResponse,
  IVoucherCardResV2,
  IMoneycardsRes,
  UserNotificationsPreferencesResponse,
  UserGetPaymentMethodsResponse,
  FetchUserCouponsResponse,
  DishAdditionalInfoResponse,
  MoneyCard,
  BillingPermissionsListResponse,
  AddBillingPermissionUserResponse,
  RevealMoneyCardResponse,
  LoyaltyProgramBalanceResponse,
  LoyaltyProgramConvertResponse,
} from './interfaces/responses';

export const updateUserDetails = async (params: IUpdateUserParams, config: IUserConfig) => {
  const response = await put<IUpdateUserRes>('v1/User/Details', params, config);
  return validateAxiosAndReturnResponse<IUpdateUserRes>(response);
};

export const removeUserAccount = (config: IUserConfig) => apiDelete('v1/User', undefined, config);

export const marketingSubscription = async (subscribe: boolean, unsubscribeToken: string) => {
  const response = await patch<string>(`v1/User/marketingSubscription/${subscribe}`, {}, {}, {headers: {unsubscribeToken}});
  return validateAxiosAndReturnResponse<string>(response);
};

export const updateMarketingSubscriptionPreferences = async (wantPromotions: boolean) => {
  await patch<string>(`v1/User/marketingSubscriptionPreferences/${wantPromotions}`);
};

export const updateResReviewNotificationPreferences = async (wantPromotions: boolean) => {
  await patch<string>(`v1/User/ResReviewNotificationPreferences/${wantPromotions}`);
};

export const fetchRestaurantGroups = async (params: IRestaurantGroupsParams) => {
  const response = await get<IRestaurantGroupsRes>(`v1/RestaurantGroups/${params.type}`);
  return validateAxiosAndReturnResponse<IRestaurantGroupsRes>(response);
};

export const fetchRestaurantMenu = async ({id, addressId, dateTime}: IGetRestaurantMenuParams) => {
  const options: Omit<IGetRestaurantMenuParams, 'id'> = {
    addressId,
    dateTime,
  };
  const response = await get<IRestaurantMenuRes>(`v1/Restaurants/${id}/Menu`, options);
  return validateAxiosAndReturnResponse<IRestaurantMenuRes>(response);
};

export const fetchRestaurant = async ({id, addressId, longitude, latitude}: IGetRestaurantParams) => {
  const options: Omit<IGetRestaurantParams, 'id'> = {
    addressId,
    longitude,
    latitude,
  };
  const response = await get<RestaurantFromGet>(`v1/Restaurants/${id}`, options);
  return validateAxiosAndReturnResponse<RestaurantFromGet>(response);
};

export const fetchMarketingBanners = async (params: IMarketingBannerParams, headers?: AxiosRequestConfig['headers']) => {
  const response = await get<IMarketingBannerRes>('v1/MarketingBanners', params, headers);
  return validateAxiosAndReturnResponse<IMarketingBannerRes>(response);
};

export const cancelActiveOrderByUser = (params: IOrderParams) => patch(`v1/Orders/${params.orderId}/Cancel`);

// This request doesnt require a data object, a validation of an AxiosResponse shouldnt be performed here
export const getRefreshToken = async () => post<IRefreshTokenRes>('v1/Authentication/RefreshToken');

export const fetchOrderData = async (params: IOrderParams) => {
  const response = await get<IOrderRes>(`v2/Orders/${params.orderId}`);
  return validateAxiosAndReturnResponse<IOrderRes>(response);
};

export const fetchVoucherCardByOrderId = async (params: {orderId: OrderData['orderId']}) => {
  const response = await get<IVoucherCardResV2>(`v2/VoucherCards?orderId=${params.orderId}`);
  return validateAxiosAndReturnResponse<IVoucherCardResV2>(response);
};

export const fetchActiveOrders = async () => {
  const response = await get<{orderBanners: ActiveOrder[]}>('v1/Orders/Banners');
  return validateAxiosAndReturnResponse<{orderBanners: ActiveOrder[]}>(response);
};

export const fetchScooberJobV2 = async (orderId: OrderData['orderId']) => {
  const response = await get<OrderScooberJob>(`v2/Orders/${orderId}/ScooberJob`);
  return validateAxiosAndReturnResponse<OrderScooberJob>(response);
};

export const fetchDiscountInfoById = async ({discountId}: {discountId: OrderData['discountCoupon']['id']}) => {
  const response = await get<IDiscountResponse>(`v1/Discounts/Coupons/${discountId}`);
  return validateAxiosAndReturnResponse<IDiscountResponse>(response);
};

export const fetchBrandingPageData = async (params: IFetchBrandingPageDataParams) => {
  const response = await get<IFetchBrandingPageDataResponse>(`v1/Companies/CompanyBranding?companyBrandingContentType=${params.pageType}&token=${params.token}`);
  return validateAxiosAndReturnResponse<IFetchBrandingPageDataResponse>(response);
};

export const loadTenbisCredit = async (params: ILoadTenbisCredit) => patch<ILoadTenbisCreditResponse>('v3/Payments/LoadTenbisCredit', {}, params);

export const validateMerchant = async (url: string) => {
  const response = await get<ApplePayMerchantSessionResponse>(`v1/Payments/ApplePayMerchantSession?validationURL=${url}`);
  return validateAxiosAndReturnResponse<ApplePayMerchantSessionResponse>(response);
};

export const fetchSystemAnnouncement = async (params: IMarketingBannerParams & {deliveryMethod?: DeliveryMethods}) => {
  const response = await get<ISystemAnnouncement>('v1/announcements', params);
  return validateAxiosAndReturnResponse<ISystemAnnouncement>(response);
};

export const getTrackerContext = async (trackerToken: string) => {
  const response = await get<TrackerContextResponse>(`v1/Orders/Tracker/${trackerToken}/Context`);
  return validateAxiosAndReturnResponse<TrackerContextResponse>(response);
};

export const getTrackerRoute = async (trackerToken: string, latitude = 0, longitude = 0) => {
  const response = await get<TrackerRouteResponse>(`v1/Orders/Tracker/${trackerToken}/Route?latitude=${latitude}&longitude=${longitude}`);
  return validateAxiosAndReturnResponse<TrackerRouteResponse>(response);
};

export const updateAutoCreditSubscription = async (params: IAutoCreditSubscription) => {
  const response = await patch<IAutoCreditSubscriptionResponse>('v1/Payments/MoneyCards/AutoCreditSubscription', {}, params);
  return validateAxiosAndReturnResponse<IAutoCreditSubscriptionResponse>(response);
};

export const fetchUnusedVouchers = async (restaurantId: RestaurantFromGet['id']) => {
  const response = await get<IUnusedVouchersResponse>(`v1/VoucherCards/GetUserVouchers?restaurantId=${restaurantId}`);
  return validateAxiosAndReturnResponse<IUnusedVouchersResponse>(response);
};

export const fetchMoneycards = async () => {
  const response = await get<IMoneycardsRes>('v1/Payments/Moneycards');
  return validateAxiosAndReturnResponse<IMoneycardsRes>(response);
};

export const fetchUserNotificationsPreferences = async () => {
  const response = await get<UserNotificationsPreferencesResponse>('v1/User/NotificationsPreferences');
  return validateAxiosAndReturnResponse<UserNotificationsPreferencesResponse>(response);
};

export const updateUserNotificationsPreferences = async ({enabled, communicationMethod}: UpdateUserNotificationsPreferencesParams) => {
  await patch<UserNotificationsPreferencesResponse>('v1/User/NotificationPreferences', {enabled, communicationMethod});
};

export const fetchUserPaymentMethods = async () => {
  const response = await get<UserGetPaymentMethodsResponse>('v1/Payments');
  return validateAxiosAndReturnResponse(response);
};

export const deleteUserCreditCard = async (creditCardId: number) => {
  await apiDelete(`v1/Payments/CreditCard/${creditCardId}`);
};

export const fetchUserCoupons = async () => {
  const response = await get<FetchUserCouponsResponse>('/v1/Discounts/Coupons');
  return validateAxiosAndReturnResponse(response);
};

export const fetchItemAdditionalInfo = async (itemId: Dish['dishId']) => {
  const response = await get<DishAdditionalInfoResponse>(`v1/Restaurants/ItemInformation/${itemId}`);
  return validateAxiosAndReturnResponse<DishAdditionalInfoResponse>(response);
};

export const validateResetPasswordToken = async (sessionToken: string) => {
  await post('v1/User/Password/ValidateResetToken', {
    sessionToken,
  });
};

export const resetUserPassword = async (sessionToken: string, newPassword: string) => {
  await post('v1/User/Password/ResetComplete', {
    sessionToken,
    newPassword,
  });
};

// OTL

export const getOtlRuleManagement = async (moneycardId: string) => {
  const response = await post<MoneycardOtlRuleManagement>('v1/Payments/Moneycards/otl/management', {
    encryptedMoneycardId: moneycardId,
  });
  return validateAxiosAndReturnResponse<MoneycardOtlRuleManagement>(response);
};

export const upsertOtlRule = async (params: UpsertOverTheLimitRuleParams) => {
  await put('v1/Payments/Moneycards/otl', params);
};

export const disableOtlRule = async (ruleId: number) => {
  await post('v1/Payments/Moneycards/otl/disable', {ruleId});
};

export const replaceMoneycard = async (moneycardId: MoneyCard['id'], addressId: Address['addressId']) => {
  await post('v1/Payments/Moneycards/replace', {
    encryptedMoneycardId: moneycardId,
    addressId,
  });
};

export const revealMoneycard = async (encryptedMcId: MoneyCard['id']) => {
  const res = await post<RevealMoneyCardResponse>('v1/Payments/Moneycards/reveal', {
    encryptedMcId,
  });
  return validateAxiosAndReturnResponse<RevealMoneyCardResponse>(res);
};

export const reIssueMoneycard = async (moneycardId: MoneyCard['id'], addressId: Address['addressId']) => {
  await post('v1/Payments/Moneycards/reissue', {
    encryptedMoneycardId: moneycardId,
    addressId,
  });
};

export const cancelMoneycard = async (moneycardId: MoneyCard['id']) => {
  await post('v1/Payments/Moneycards/delete', {
    encryptedMoneycardId: moneycardId,
  });
};

export const getBillingPermissionsList = async (moneycardId: string) => {
  const response = await post<BillingPermissionsListResponse>('v1/Payments/Moneycards/billing-permissions/list', {
    encryptedMoneycardId: moneycardId,
  });
  return validateAxiosAndReturnResponse<BillingPermissionsListResponse>(response);
};

export const addBillingPermissionUser = async (moneycardId: string, email: User['email']) => {
  const response = await post<AddBillingPermissionUserResponse>('v1/Payments/Moneycards/billing-permissions/add', {
    encryptedMoneycardId: moneycardId,
    email,
  });
  return response;
};

export const removeBillingPermissionUser = async (moneycardId: string, userId: User['userId']) => {
  const response = await post('v1/Payments/Moneycards/billing-permissions/remove', {
    encryptedMoneycardId: moneycardId,
    userIdToRemove: userId,
  });
  return response;
};

export const fetchLoyaltyProgramBalance = async () => {
  const response = await get<LoyaltyProgramBalanceResponse>('v1/LoyaltyProgram/Balance');
  return validateAxiosAndReturnResponse<LoyaltyProgramBalanceResponse>(response);
};

export const loyaltyProgramConvert = async (unitsToConvert: string) => {
  const response = await post<LoyaltyProgramConvertResponse>('v1/LoyaltyProgram/Convert', {
    unitsToConvert,
  });
  return validateAxiosAndReturnResponse<LoyaltyProgramConvertResponse>(response);
};
